import {
  Component,
  OnInit,
  ChangeDetectorRef,
  NgZone,
  ChangeDetectionStrategy,
} from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environment/environment';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { finalize } from 'rxjs/operators';

interface RespondEventModule {
  module_id: number;
  name: string;
  description?: string;
  maintenance_minutes?: number;
  prior_instructor?: boolean;
  prior_completed?: boolean;
}

interface RespondEvent {
  event_id: number;
  guid: string;
  start_date: string;
  end_date: string;
  event_name?: string;
  location?: string;
  modules?: RespondEventModule[];
}

@Component({
  selector: 'app-duna-event-instructors-respond',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatIconModule,
  ],
  templateUrl: './duna-event-instructors-respond.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-event-instructors-respond.component.scss',
})
export class DunaEventInstructorsRespondComponent implements OnInit {
  guid: string | null = null;
  loading = true;
  error: string | null = null;
  responseDeadline: string | null = null;
  /** Pre-formatted for display (avoids DatePipe / Safari issues with SQL-style datetimes). */
  deadlineDisplay: string | null = null;
  status: string | null = null;
  events: RespondEvent[] = [];
  selectedEventIds: Set<number> = new Set();
  saving = false;
  saveMessage: string | null = null;
  saveHadError = false;

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private snackBar: MatSnackBar
  ) {}

  get apiUrl(): string {
    return environment.apiUrl;
  }

  ngOnInit(): void {
    // Read GUID only from window.location.pathname so the request always matches the address bar.
    this.guid = this.getInvitationGuidFromPath();
    if (!this.guid) {
      this.loading = false;
      this.error = 'Invitation findes ikke.';
      this.cdr.detectChanges();
      return;
    }
    this.load();
  }

  /** Get invitation GUID from current browser path (window.location.pathname). No other source. */
  private getInvitationGuidFromPath(): string | null {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const parts = path.split('/').filter(Boolean);
    if (parts.length >= 2 && parts[0] === 'invitation') {
      const guid = parts[1];
      return guid;
    }
    if (
      parts.length === 1 &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        parts[0]
      )
    ) {
      return parts[0];
    }
    return null;
  }

  load(): void {
    const url = `${this.apiUrl}/events/instructor-invitations/${this.guid}/respond`;
    this.loading = true;
    this.error = null;
    this.deadlineDisplay = null;
    this.http
      .get<{
        response_deadline: string;
        status: string;
        events: RespondEvent[];
        selected_event_ids?: number[];
      }>(url)
      .pipe(
        finalize(() => {
          this.ngZone.run(() => {
            this.loading = false;
            this.cdr.markForCheck();
          });
        })
      )
      .subscribe({
        next: (res) => {
          this.ngZone.run(() => {
            this.responseDeadline = res.response_deadline ?? null;
            this.deadlineDisplay = this.formatDeadlineForDisplay(
              this.responseDeadline
            );
            this.status = res.status ?? null;
            this.events = res.events ?? [];
            const preselected = res.selected_event_ids ?? [];
            this.selectedEventIds = new Set(
              preselected.filter((id) => Number.isFinite(id))
            );
            this.cdr.markForCheck();
          });
        },
        error: (err) => {
          this.ngZone.run(() => {
            const msg = err?.error?.message;
            if (msg) {
              this.error = msg;
            } else if (err?.status === 404) {
              this.error = 'Invitationen findes ikke længere eller er slettet.';
            } else if (err?.status === 403) {
              this.error =
                'Du har ikke længere adgang til denne invitation. Du er måske blevet fjernet fra invitationen.';
            } else {
              this.error =
                'Du har ikke adgang til denne invitation, eller den findes ikke. Log ind som inviteret bruger.';
            }
            this.cdr.markForCheck();
          });
        },
      });
  }

  toggle(eventId: number): void {
    if (this.selectedEventIds.has(eventId)) {
      this.selectedEventIds.delete(eventId);
    } else {
      this.selectedEventIds.add(eventId);
    }
    this.selectedEventIds = new Set(this.selectedEventIds);
  }

  isSelected(eventId: number): boolean {
    return this.selectedEventIds.has(eventId);
  }

  save(): void {
    this.saveMessage = null;
    this.saveHadError = false;
    this.saving = true;
    this.cdr.markForCheck();
    this.http
      .post<{ saved: number }>(
        `${this.apiUrl}/events/instructor-invitations/${this.guid}/responses`,
        { event_ids: Array.from(this.selectedEventIds) }
      )
      .pipe(
        finalize(() => {
          this.ngZone.run(() => {
            this.saving = false;
            this.cdr.markForCheck();
          });
        })
      )
      .subscribe({
        next: () => {
          this.ngZone.run(() => {
            this.saveHadError = false;
            this.saveMessage = 'Dine svar er gemt og modtaget.';
            this.snackBar.open(
              'Vi har modtaget dine svar. Tak. Du kan lukke denne side.',
              'OK',
              { duration: 12000 }
            );
            this.cdr.markForCheck();
          });
        },
        error: (err) => {
          this.ngZone.run(() => {
            this.saveHadError = true;
            const msg = err?.error?.message;
            if (msg) {
              this.saveMessage = msg;
            } else if (err?.status === 404) {
              this.saveMessage =
                'Invitationen findes ikke længere eller er slettet.';
            } else if (err?.status === 403) {
              this.saveMessage =
                'Du har ikke længere adgang til denne invitation. Du er måske blevet fjernet fra invitationen.';
            } else {
              this.saveMessage = 'Kunne ikke gemme svar.';
            }
            this.cdr.markForCheck();
          });
        },
      });
  }

  eventTitle(ev: RespondEvent): string {
    return ev.event_name?.trim() || ev.guid || 'Event';
  }

  eventScheduleLine(ev: RespondEvent): string {
    const timeRange = this.formatEventTimeRange(ev.start_date, ev.end_date);
    const duration = this.formatEventDurationHours(ev.start_date, ev.end_date);
    return `${timeRange} · ${duration}`;
  }

  formatModuleMinutes(minutes: number): string {
    if (!Number.isFinite(minutes) || minutes <= 0) {
      return '';
    }
    if (minutes < 60) {
      return `${minutes} min.`;
    }
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (m === 0) {
      return `${h} ${h === 1 ? 'time' : 'timer'}`;
    }
    return `${h} t. ${m} min.`;
  }

  /** SQL / API datetimes are not reliably parsed by all browsers unless normalized. */
  private parseApiDate(raw: string | null | undefined): Date | null {
    if (!raw) {
      return null;
    }
    const normalized = raw.trim().replace(' ', 'T');
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private formatDeadlineForDisplay(raw: string | null): string | null {
    if (!raw) {
      return null;
    }
    const trimmed = raw.trim();
    const datePart = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (datePart) {
      const year = Number(datePart[1]);
      const month = Number(datePart[2]);
      const day = Number(datePart[3]);
      const localCalendar = new Date(year, month - 1, day);
      if (!Number.isNaN(localCalendar.getTime())) {
        return localCalendar.toLocaleDateString('da-DK', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      }
    }
    const d = this.parseApiDate(trimmed);
    if (d) {
      return d.toLocaleDateString('da-DK', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }
    const fallbackDate = trimmed.split(/[T\s]/)[0];
    return fallbackDate || trimmed;
  }

  private formatEventTimeRange(
    startRaw: string | null | undefined,
    endRaw: string | null | undefined
  ): string {
    const start = this.parseApiDate(startRaw);
    const end = this.parseApiDate(endRaw);
    if (!start || !end) {
      return 'Tidspunkt ukendt';
    }

    const sameDay = start.toDateString() === end.toDateString();
    if (sameDay) {
      return `${start.toLocaleDateString(
        'da-DK'
      )} kl. ${start.toLocaleTimeString('da-DK', {
        hour: '2-digit',
        minute: '2-digit',
      })}-${end.toLocaleTimeString('da-DK', {
        hour: '2-digit',
        minute: '2-digit',
      })}`;
    }

    return `${start.toLocaleString('da-DK', {
      dateStyle: 'short',
      timeStyle: 'short',
    })} - ${end.toLocaleString('da-DK', {
      dateStyle: 'short',
      timeStyle: 'short',
    })}`;
  }

  private formatEventDurationHours(
    startRaw: string | null | undefined,
    endRaw: string | null | undefined
  ): string {
    const start = this.parseApiDate(startRaw);
    const end = this.parseApiDate(endRaw);
    if (!start || !end) {
      return 'timer ukendt';
    }

    const totalMinutes = Math.round(
      (end.getTime() - start.getTime()) / (1000 * 60)
    );
    if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) {
      return '0 timer';
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (minutes === 0) {
      return `${hours} ${hours === 1 ? 'time' : 'timer'}`;
    }

    return `${hours} ${hours === 1 ? 'time' : 'timer'} ${minutes} minutter`;
  }
}
